<script setup>
import {
  onMounted, watch, ref, nextTick,
} from 'vue';
import gsap from 'gsap';

const props = defineProps({
  x: {
    type: Number,
    required: true,
  },
  y: {
    type: Number,
    required: true,
  },
  city: {
    type: String,
    default: '',
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const markerCircle = ref(null);
const pulseCircle = ref(null);

const startIdleAnimation = () => {
  if (!pulseCircle.value) return;

  // Kill any existing animations on the pulse circle
  gsap.killTweensOf(pulseCircle.value);

  // Create a new pulse timeline
  const tl = gsap.timeline({ repeat: -1 });

  tl.to(pulseCircle.value, {
    attr: { r: 64, opacity: 0 },
    duration: 2,
    ease: 'power1.out',
  })
    .set(pulseCircle.value, {
      attr: { r: 8, opacity: 0.5 },
    });
};

const playActiveAnimation = () => {
  if (!markerCircle.value || !pulseCircle.value) return;

  // Kill existing animations
  gsap.killTweensOf([markerCircle.value, pulseCircle.value]);

  // Reset initial state
  gsap.set(markerCircle.value, { attr: { r: 0 } });
  gsap.set(pulseCircle.value, { attr: { r: 0, opacity: 0.5 } });

  const tl = gsap.timeline({
    onComplete: () => {
      // Transition to idle animation after entrance
      startIdleAnimation();
    },
  });

  // Pop up effect
  tl.to(markerCircle.value, {
    attr: { r: 32 },
    duration: 0.6,
    ease: 'back.out(1.7)',
  });

  // Initial large pulse wave
  tl.to(pulseCircle.value, {
    attr: { r: 128, opacity: 0 },
    duration: 1.5,
    ease: 'power2.out',
  }, '<'); // Start at same time
};

watch(() => props.isActive, async (newActive) => {
  await nextTick();
  if (newActive) {
    playActiveAnimation();
  }
});

onMounted(async () => {
  await nextTick();
  // Explicitly set initial state to avoid invisible markers if animation fails
  if (markerCircle.value) gsap.set(markerCircle.value, { attr: { r: 32 } });

  if (props.isActive) {
    playActiveAnimation();
  } else {
    startIdleAnimation();
  }
});
</script>

<template>
  <g :transform="`translate(${x}, ${y})`">
    <!-- Pulse Circle (Behind) -->
    <circle
      ref="pulseCircle"
      r="32"
      fill="#CD3198"
      opacity="0.5"
    />
    <!-- Main Marker Circle -->
    <circle
      ref="markerCircle"
      r="16"
      fill="#CD3198"
    />
  </g>
</template>
